(() => {
  const ITEM_SELECTOR = '[data-qty-limit-item="true"]';
  const WARNING_CLASS = 'qty-limit-warning';
  const SUMMARY_ID = 'QuantityLimitSummary';
  const DISABLED_CLASS = 'qty-limit-checkout-disabled';
  const CHECKOUT_SELECTOR = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    '#checkout',
    '#CartDrawer-Checkout',
    '.cart__checkout-button',
    '[data-checkout]',
    'a[href*="/checkout"]',
    'a[href*="checkoutchamp"]',
    'a[href*="checkout-champ"]'
  ].join(',');

  const toInt = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getQuantity = (item) => {
    const input = item.querySelector('input[name="updates[]"], .quantity__input, input[type="number"]');
    return Math.max(0, toInt(input ? input.value : item.dataset.quantity));
  };

  const getTitle = (item) => {
    return item.dataset.productTitle || item.querySelector('.cart-item__name')?.textContent?.trim() || 'this item';
  };

  const findCheckoutButtons = () => Array.from(document.querySelectorAll(CHECKOUT_SELECTOR))
    .filter((button) => {
      if (!button) return false;
      if (button.closest('form[action*="/cart/add"]')) return false;
      const text = (button.textContent || button.value || '').toLowerCase();
      const href = (button.getAttribute('href') || '').toLowerCase();
      return text.includes('checkout') || href.includes('checkout') || button.name === 'checkout' || button.id === 'checkout' || button.id === 'CartDrawer-Checkout';
    });

  const getWarningNode = (item) => {
    let warning = item.querySelector(`.${WARNING_CLASS}`);
    if (warning) return warning;

    warning = document.createElement('div');
    warning.className = WARNING_CLASS;
    warning.setAttribute('role', 'alert');
    warning.hidden = true;

    const details = item.querySelector('.cart-item__details') || item.querySelector('.cart-drawer-item__right') || item;
    details.appendChild(warning);
    return warning;
  };

  const getSummaryTargets = () => {
    const targets = [];
    const drawerCtas = document.querySelector('#CartDrawer-Checkout')?.closest('.cart__ctas');
    const pageCtas = document.querySelector('#checkout')?.closest('.cart__ctas');
    if (drawerCtas) targets.push(drawerCtas);
    if (pageCtas && pageCtas !== drawerCtas) targets.push(pageCtas);
    return targets;
  };

  const ensureSummary = (target) => {
    let summary = target.querySelector(`.${SUMMARY_ID}`);
    if (summary) return summary;

    summary = document.createElement('div');
    summary.className = `${SUMMARY_ID} qty-limit-summary`;
    summary.setAttribute('role', 'alert');
    summary.hidden = true;
    target.insertBefore(summary, target.firstElementChild || null);
    return summary;
  };

  const getLinePriority = (item) => {
    const drawer = item.closest('cart-drawer');
    const drawerActive = drawer && (drawer.classList.contains('active') || drawer.hasAttribute('open') || drawer.getAttribute('aria-hidden') === 'false');
    const isCartPageLine = !drawer && !!item.closest('cart-items, #main-cart-items, form#cart');
    const isVisible = !!(item.offsetWidth || item.offsetHeight || item.getClientRects().length);

    if (drawerActive) return 100 + (isVisible ? 10 : 0);
    if (isCartPageLine) return 80 + (isVisible ? 10 : 0);
    if (drawer) return 60 + (isVisible ? 10 : 0);
    return isVisible ? 20 : 0;
  };

  const getCanonicalLines = () => {
    const lines = new Map();
    document.querySelectorAll(ITEM_SELECTOR).forEach((item, index) => {
      const max = toInt(item.dataset.maxQtyPerOrder);
      const productId = item.dataset.productId;
      if (!productId || max <= 0) return;

      const lineKey = item.dataset.lineKey || `${productId}:${item.dataset.index || item.id || index}`;
      const groupKey = item.dataset.qtyLimitGroup || productId;
      const groupTitle = item.dataset.qtyLimitGroupTitle || getTitle(item);
      const candidate = {
        item,
        lineKey,
        productId,
        groupKey,
        groupTitle,
        max,
        title: getTitle(item),
        quantity: getQuantity(item),
        priority: getLinePriority(item)
      };
      const current = lines.get(lineKey);
      if (!current || candidate.priority >= current.priority) lines.set(lineKey, candidate);
    });
    return Array.from(lines.values());
  };

  const getGroups = () => {
    const groups = new Map();
    getCanonicalLines().forEach((line) => {
      if (!groups.has(line.groupKey)) {
        groups.set(line.groupKey, {
          groupKey: line.groupKey,
          max: line.max,
          title: line.groupTitle,
          quantity: 0,
          items: []
        });
      }

      const group = groups.get(line.groupKey);
      group.max = Math.min(group.max || line.max, line.max);
      group.quantity += line.quantity;
      group.items.push(line.item);
    });
    return Array.from(groups.values());
  };

  const setCheckoutState = (invalidGroups) => {
    const invalid = invalidGroups.length > 0;
    const first = invalidGroups[0];
    const summaryText = invalid
      ? `${first.title} has a maximum of ${first.max} total per order. Please reduce the quantity before checkout.`
      : '';

    getSummaryTargets().forEach((target) => {
      const summary = ensureSummary(target);
      summary.textContent = summaryText;
      summary.hidden = !invalid;
    });

    findCheckoutButtons().forEach((button) => {
      if (invalid) {
        if (!button.dataset.qtyLimitOriginalLabel) {
          button.dataset.qtyLimitOriginalLabel = button.textContent.trim() || button.value || '';
          button.dataset.qtyLimitOriginalDisabled = button.disabled ? 'true' : 'false';
        }
        button.classList.add(DISABLED_CLASS);
        button.setAttribute('aria-disabled', 'true');
        button.dataset.qtyLimitBlocked = 'true';
        if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
          button.disabled = true;
        }
        const label = button.querySelector('.button__label');
        if (label) {
          label.textContent = 'Fix quantity limits to checkout';
        } else if (button.tagName === 'INPUT' && button.value) {
          button.value = 'Fix quantity limits to checkout';
        } else if (button.tagName === 'BUTTON' && button.childElementCount === 0 && button.textContent.trim()) {
          button.textContent = 'Fix quantity limits to checkout';
        }
      } else if (button.dataset.qtyLimitBlocked === 'true') {
        button.classList.remove(DISABLED_CLASS);
        button.removeAttribute('aria-disabled');
        delete button.dataset.qtyLimitBlocked;
        if (button.tagName === 'BUTTON' || button.tagName === 'INPUT') {
          button.disabled = button.dataset.qtyLimitOriginalDisabled === 'true';
        }
        if (button.dataset.qtyLimitOriginalLabel) {
          const label = button.querySelector('.button__label');
          if (label) label.textContent = button.dataset.qtyLimitOriginalLabel;
          else if (button.tagName === 'INPUT') button.value = button.dataset.qtyLimitOriginalLabel;
          else if (button.childElementCount === 0) button.textContent = button.dataset.qtyLimitOriginalLabel;
          delete button.dataset.qtyLimitOriginalLabel;
        }
        delete button.dataset.qtyLimitOriginalDisabled;
      }
    });

    document.documentElement.classList.toggle('qty-limit-invalid-cart', invalid);
  };

  const validate = () => {
    const invalidGroups = [];
    const groups = getGroups();
    const invalidByGroup = new Map();

    groups.forEach((group) => {
      const invalid = group.quantity > group.max;
      if (invalid) {
        invalidGroups.push(group);
        invalidByGroup.set(group.groupKey, group);
      }
    });

    document.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      const productId = item.dataset.productId;
      const groupKey = item.dataset.qtyLimitGroup || productId;
      const group = invalidByGroup.get(groupKey);
      const warning = getWarningNode(item);
      if (group) {
        warning.textContent = `Maximum ${group.max} total per order across ${group.title}. Please reduce quantity before checkout.`;
        warning.hidden = false;
        item.classList.add('qty-limit-item-invalid');
      } else {
        warning.textContent = '';
        warning.hidden = true;
        item.classList.remove('qty-limit-item-invalid');
      }
    });

    setCheckoutState(invalidGroups);
    return invalidGroups.length === 0;
  };

  let validateTimer;
  const scheduleValidate = () => {
    clearTimeout(validateTimer);
    validateTimer = setTimeout(validate, 80);
  };

  document.addEventListener('input', (event) => {
    if (event.target && event.target.matches('input[name="updates[]"], .quantity__input')) scheduleValidate();
  }, true);

  document.addEventListener('change', (event) => {
    if (event.target && event.target.matches('input[name="updates[]"], .quantity__input')) scheduleValidate();
  }, true);

  document.addEventListener('click', (event) => {
    const checkoutTarget = event.target.closest(CHECKOUT_SELECTOR);
    if (checkoutTarget && checkoutTarget.dataset.qtyLimitBlocked === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      validate();
      document.querySelector('.qty-limit-warning:not([hidden]), .qty-limit-summary:not([hidden])')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      return false;
    }

    if (event.target.closest('.quantity__button, cart-remove-button, [data-cart-update], [data-index]')) {
      scheduleValidate();
      setTimeout(scheduleValidate, 450);
      setTimeout(scheduleValidate, 1000);
    }
  }, true);

  ['cart:refresh', 'cart:updated', 'cartQuantityUpdated', 'shopify:section:load'].forEach((eventName) => {
    document.addEventListener(eventName, scheduleValidate);
  });

  const observer = new MutationObserver(scheduleValidate);
  const start = () => {
    validate();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.QuantityLimitGuard = { validate, scheduleValidate };
})();
